import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Edit2,
  Trash2,
  Shield,
  ShieldCheck,
  Eye,
  ChevronLeft,
  Loader2,
  Search,
  MoreVertical,
  UserX,
  UserCheck,
  Mail,
  Calendar,
  MessageSquare,
  Database,
  Image,
  Layers,
  Printer,
  Check,
  X,
  Settings,
  Crown,
  Key,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main theme color
const THEME_COLOR = "#5990DC";

// Available permissions
const AVAILABLE_PERMISSIONS = [
  { id: "support", label: "Support Chat", icon: MessageSquare, description: "Access customer support conversations" },
  { id: "orders", label: "Orders", icon: Database, description: "Manage customer orders" },
  { id: "images", label: "Images & Trash", icon: Image, description: "View and manage customer photos" },
  { id: "templates", label: "Templates", icon: Layers, description: "Create and edit design templates" },
  { id: "production", label: "Production", icon: Printer, description: "Generate designs and PDFs" },
];

// Role definitions with descriptions
const ROLE_CONFIG = {
  admin: { 
    color: `bg-[${THEME_COLOR}]`, 
    gradient: "bg-gradient-to-r from-amber-500 to-orange-500",
    textColor: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    icon: Crown,
    label: "Administrator",
    description: "Full access to all features including user management, settings, and all permissions"
  },
  editor: { 
    color: `bg-[${THEME_COLOR}]`,
    gradient: "bg-gradient-to-r from-blue-500 to-cyan-500", 
    textColor: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    icon: Edit2,
    label: "Editor",
    description: "Can create, edit, and manage orders, images, templates, and production. Cannot access user management or settings"
  },
  viewer: { 
    color: "bg-slate-500",
    gradient: "bg-gradient-to-r from-slate-400 to-slate-500", 
    textColor: "text-slate-600",
    bgColor: "bg-slate-50",
    borderColor: "border-slate-200",
    icon: Eye,
    label: "Viewer",
    description: "Read-only access to assigned features. Cannot make changes or delete any data"
  }
};

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "viewer",
    permissions: [],
    is_active: true
  });
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      setCurrentUser(user);
      
      if (user.role !== "admin") {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
        return;
      }
    } else {
      navigate("/login");
      return;
    }
    
    fetchUsers();
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      if (error.response?.status === 403) {
        toast.error("Access denied");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load users");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/users`, {
        ...formData,
        permissions: formData.role === "admin" ? [] : formData.permissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User created successfully");
      setShowCreateDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      const updateData = {
        name: formData.name,
        role: formData.role,
        is_active: formData.is_active,
        permissions: formData.role === "admin" ? [] : formData.permissions
      };
      
      await axios.put(`${API}/admin/users/${selectedUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User updated successfully");
      setShowEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser) return;
    
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${selectedUser.id}/reset-password`, 
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password changed successfully");
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted successfully");
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/users/${user.id}`, 
        { is_active: !user.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(user.is_active ? "User deactivated" : "User activated");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user status");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "viewer",
      permissions: [],
      is_active: true
    });
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
      permissions: user.permissions || [],
      is_active: user.is_active !== false
    });
    setShowEditDialog(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setConfirmPassword("");
    setShowPasswordDialog(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#5990DC] mx-auto mb-4" />
          <p className="text-slate-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-700 hover:bg-slate-100">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-[#5990DC]">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  User Management
                </h1>
                <p className="text-sm text-slate-500 mt-1">Manage team access and permissions</p>
              </div>
            </div>
            
            {/* Current Admin Info */}
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-xs text-slate-400">Logged in as</p>
                <p className="text-slate-700 font-medium">{currentUser?.name}</p>
              </div>
              <Button 
                onClick={() => {
                  resetForm();
                  setShowCreateDialog(true);
                }}
                className="bg-[#5990DC] hover:bg-[#4a7bc4] shadow-md"
                data-testid="create-user-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-800">{users.length}</p>
                  <p className="text-sm text-slate-500 mt-1">Total Users</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-50">
                  <Users className="h-6 w-6 text-[#5990DC]" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-800">
                    {users.filter(u => u.role === "admin").length}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">Administrators</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50">
                  <Crown className="h-6 w-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-800">
                    {users.filter(u => u.is_active !== false).length}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">Active</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50">
                  <UserCheck className="h-6 w-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-800">
                    {users.filter(u => u.is_active === false).length}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">Inactive</p>
                </div>
                <div className="p-3 rounded-xl bg-red-50">
                  <UserX className="h-6 w-6 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Role Definitions */}
        <Card className="bg-white border-slate-200 shadow-sm mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-slate-700">Role Definitions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(ROLE_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <div key={key} className={`p-4 rounded-xl ${config.bgColor} border ${config.borderColor}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${config.gradient}`}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <span className={`font-semibold ${config.textColor}`}>{config.label}</span>
                    </div>
                    <p className="text-sm text-slate-600">{config.description}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Search Bar - with autofill prevention */}
        <div className="mb-6">
          <div className="relative max-w-md">
            {/* Hidden field to capture browser autofill */}
            <input type="text" name="fake-username-field" style={{ display: 'none' }} tabIndex={-1} autoComplete="username" />
            <input type="password" name="fake-password-field" style={{ display: 'none' }} tabIndex={-1} autoComplete="current-password" />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              name="user-search-filter"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              data-lpignore="true"
              data-form-type="other"
              className="pl-11 bg-white border-slate-200 text-slate-700 h-11 rounded-xl focus:ring-2 focus:ring-[#5990DC]/30 focus:border-[#5990DC]"
              data-testid="search-users"
            />
          </div>
        </div>

        {/* Users Grid */}
        <div className="grid gap-4">
          {filteredUsers.map((user) => {
            const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.viewer;
            const RoleIcon = roleConfig.icon;
            const isCurrentUser = currentUser?.id === user.id;
            const userPermissions = user.permissions || [];
            
            return (
              <Card 
                key={user.id} 
                className={`bg-white border-slate-200 shadow-sm hover:shadow-md transition-all ${
                  !user.is_active ? 'opacity-60' : ''
                }`}
                data-testid={`user-card-${user.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-5">
                    {/* Avatar */}
                    <div className={`relative h-14 w-14 rounded-2xl ${roleConfig.gradient} flex items-center justify-center text-white font-bold text-lg shadow-md`}>
                      {getInitials(user.name)}
                      {isCurrentUser && (
                        <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </div>
                    
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-800 text-lg truncate">{user.name}</h3>
                        {isCurrentUser && (
                          <Badge className="bg-emerald-100 text-emerald-600 border-0 text-xs">You</Badge>
                        )}
                        {!user.is_active && (
                          <Badge className="bg-red-100 text-red-600 border-0 text-xs">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 flex items-center gap-2 truncate">
                        <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                        {user.email}
                      </p>
                    </div>
                    
                    {/* Role Badge */}
                    <div className="hidden md:block">
                      <Badge className={`${roleConfig.bgColor} ${roleConfig.textColor} ${roleConfig.borderColor} border px-3 py-1.5`}>
                        <RoleIcon className="h-3.5 w-3.5 mr-1.5" />
                        {roleConfig.label}
                      </Badge>
                    </div>
                    
                    {/* Permissions Preview */}
                    <div className="hidden lg:flex items-center gap-1">
                      {user.role === "admin" ? (
                        <Badge className="bg-amber-50 text-amber-600 border-amber-200 border text-xs">
                          Full Access
                        </Badge>
                      ) : (
                        AVAILABLE_PERMISSIONS.slice(0, 3).map(perm => {
                          const hasAccess = userPermissions.includes(perm.id);
                          const Icon = perm.icon;
                          return (
                            <div 
                              key={perm.id}
                              className={`p-1.5 rounded-lg ${hasAccess ? 'bg-emerald-50 text-emerald-500' : 'bg-slate-100 text-slate-300'}`}
                              title={`${perm.label}: ${hasAccess ? 'Enabled' : 'Disabled'}`}
                            >
                              <Icon className="h-3.5 w-3.5" />
                            </div>
                          );
                        })
                      )}
                      {user.role !== "admin" && userPermissions.length > 3 && (
                        <span className="text-xs text-slate-400">+{userPermissions.length - 3}</span>
                      )}
                    </div>
                    
                    {/* Last Login */}
                    <div className="hidden xl:block text-right min-w-[120px]">
                      <p className="text-xs text-slate-400">Last login</p>
                      <p className="text-sm text-slate-600">{formatDate(user.last_login)}</p>
                    </div>
                    
                    {/* Actions */}
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl"
                          data-testid={`user-actions-${user.id}`}
                        >
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent 
                        align="end" 
                        className="bg-white border-slate-200 w-48 shadow-lg"
                        onCloseAutoFocus={(e) => e.preventDefault()}
                      >
                        <DropdownMenuItem 
                          onSelect={(e) => {
                            e.preventDefault();
                            openEditDialog(user);
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          className="text-slate-600 focus:bg-slate-50 focus:text-slate-800 cursor-pointer"
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit User
                        </DropdownMenuItem>
                        
                        <DropdownMenuItem 
                          onSelect={(e) => {
                            e.preventDefault();
                            openPasswordDialog(user);
                          }}
                          onMouseDown={(e) => e.preventDefault()}
                          className="text-slate-600 focus:bg-slate-50 focus:text-slate-800 cursor-pointer"
                        >
                          <Key className="h-4 w-4 mr-2" />
                          Change Password
                        </DropdownMenuItem>
                        
                        {!isCurrentUser && (
                          <>
                            <DropdownMenuItem 
                              onSelect={(e) => {
                                e.preventDefault();
                                handleToggleActive(user);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              className="text-slate-600 focus:bg-slate-50 focus:text-slate-800 cursor-pointer"
                            >
                              {user.is_active !== false ? (
                                <>
                                  <UserX className="h-4 w-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <UserCheck className="h-4 w-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            
                            <DropdownMenuSeparator className="bg-slate-100" />
                            
                            <DropdownMenuItem 
                              onSelect={(e) => {
                                e.preventDefault();
                                openDeleteDialog(user);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              className="text-red-500 focus:bg-red-50 focus:text-red-600 cursor-pointer"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete User
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {filteredUsers.length === 0 && (
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardContent className="py-16 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-500">
                  {searchQuery ? "No users found" : "No users yet"}
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  {searchQuery ? "Try a different search term" : "Add your first team member"}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 rounded-xl bg-[#5990DC]">
                <UserPlus className="h-5 w-5 text-white" />
              </div>
              Create New User
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Add a new team member and set their access permissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Full Name *</Label>
                <Input
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-slate-50 border-slate-200 text-slate-800"
                  data-testid="create-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800" data-testid="create-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="admin" className="text-slate-700 focus:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-amber-500" />
                        Administrator
                      </div>
                    </SelectItem>
                    <SelectItem value="editor" className="text-slate-700 focus:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <Edit2 className="h-4 w-4 text-blue-500" />
                        Editor
                      </div>
                    </SelectItem>
                    <SelectItem value="viewer" className="text-slate-700 focus:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-slate-500" />
                        Viewer
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-600">Email *</Label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-slate-50 border-slate-200 text-slate-800"
                data-testid="create-user-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-600">Password *</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-slate-50 border-slate-200 text-slate-800"
                data-testid="create-user-password"
              />
            </div>
            
            {/* Permissions Section */}
            {formData.role !== "admin" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-600">Access Permissions</Label>
                  <span className="text-xs text-slate-400">
                    {formData.permissions.length} of {AVAILABLE_PERMISSIONS.length} enabled
                  </span>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 space-y-2">
                  {AVAILABLE_PERMISSIONS.map((perm) => {
                    const Icon = perm.icon;
                    const isChecked = formData.permissions.includes(perm.id);
                    return (
                      <div 
                        key={perm.id}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${
                          isChecked ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => togglePermission(perm.id)}
                      >
                        <Checkbox 
                          checked={isChecked}
                          className="border-slate-300 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                        />
                        <div className={`p-1.5 rounded-lg ${isChecked ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                          <Icon className={`h-3.5 w-3.5 ${isChecked ? 'text-emerald-600' : 'text-slate-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isChecked ? 'text-slate-800' : 'text-slate-600'}`}>{perm.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {formData.role === "admin" && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <Crown className="h-5 w-5 text-amber-500" />
                  <div>
                    <p className="font-medium text-amber-700 text-sm">Full Administrator Access</p>
                    <p className="text-xs text-amber-600">Admins have access to all features</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={actionLoading}
              className="bg-[#5990DC] hover:bg-[#4a7bc4]"
              data-testid="confirm-create-user"
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 rounded-xl bg-[#5990DC]">
                <Edit2 className="h-5 w-5 text-white" />
              </div>
              Edit User
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Update user details and access permissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Full Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-slate-50 border-slate-200 text-slate-800"
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  disabled={selectedUser?.id === currentUser?.id}
                >
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-800" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="admin" className="text-slate-700 focus:bg-slate-50">Administrator</SelectItem>
                    <SelectItem value="editor" className="text-slate-700 focus:bg-slate-50">Editor</SelectItem>
                    <SelectItem value="viewer" className="text-slate-700 focus:bg-slate-50">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-600">Email</Label>
              <Input
                value={formData.email}
                disabled
                className="bg-slate-100 border-slate-200 text-slate-400"
              />
              <p className="text-xs text-slate-400">Email cannot be changed</p>
            </div>
            
            {/* Status Toggle */}
            {selectedUser?.id !== currentUser?.id && (
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div>
                  <Label className="text-slate-600">Account Status</Label>
                  <p className="text-xs text-slate-400 mt-1">Inactive users cannot log in</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            )}
            
            {/* Permissions Section */}
            {formData.role !== "admin" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-600">Access Permissions</Label>
                  <span className="text-xs text-slate-400">
                    {formData.permissions.length} of {AVAILABLE_PERMISSIONS.length} enabled
                  </span>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 space-y-2">
                  {AVAILABLE_PERMISSIONS.map((perm) => {
                    const Icon = perm.icon;
                    const isChecked = formData.permissions.includes(perm.id);
                    return (
                      <div 
                        key={perm.id}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${
                          isChecked ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-slate-200 hover:border-slate-300'
                        }`}
                        onClick={() => togglePermission(perm.id)}
                      >
                        <Checkbox 
                          checked={isChecked}
                          className="border-slate-300 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                        />
                        <div className={`p-1.5 rounded-lg ${isChecked ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                          <Icon className={`h-3.5 w-3.5 ${isChecked ? 'text-emerald-600' : 'text-slate-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isChecked ? 'text-slate-800' : 'text-slate-600'}`}>{perm.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {formData.role === "admin" && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="flex items-center gap-3">
                  <Crown className="h-5 w-5 text-amber-500" />
                  <div>
                    <p className="font-medium text-amber-700 text-sm">Full Administrator Access</p>
                    <p className="text-xs text-amber-600">Admins have access to all features</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              className="border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateUser}
              disabled={actionLoading}
              className="bg-[#5990DC] hover:bg-[#4a7bc4]"
              data-testid="confirm-edit-user"
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 rounded-xl bg-[#5990DC]">
                <Key className="h-5 w-5 text-white" />
              </div>
              Change Password
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Set a new password for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-600">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
              <p className="text-xs text-slate-400">Must be at least 6 characters</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-600">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200 text-slate-800"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPasswordDialog(false)}
              className="border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={actionLoading}
              className="bg-[#5990DC] hover:bg-[#4a7bc4]"
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl text-red-500">
              <div className="p-2 rounded-xl bg-red-100">
                <Trash2 className="h-5 w-5 text-red-500" />
              </div>
              Delete User
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              This action cannot be undone
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-xl ${ROLE_CONFIG[selectedUser.role]?.gradient || 'bg-slate-400'} flex items-center justify-center text-white font-bold`}>
                    {getInitials(selectedUser.name)}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{selectedUser.name}</p>
                    <p className="text-sm text-slate-500">{selectedUser.email}</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-4">
                Are you sure you want to permanently delete this user? All their data will be lost.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteUser}
              disabled={actionLoading}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-user"
            >
              {actionLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
